import { useState, useEffect, useCallback } from 'react';

/**
 * Custom event name for debug mode changes
 */
const PERMISSIONS_DEBUG_EVENT = 'permissions-debug-change';

/**
 * Global window property for debug mode
 */
declare global {
  interface Window {
    __PERMISSIONS_DEBUG__?: boolean;
    __togglePermissionsDebug__?: () => void;
  }
}

/**
 * Dispatches custom event to notify all hooks of debug mode change
 */
const notifyDebugChange = () => {
  window.dispatchEvent(new CustomEvent(PERMISSIONS_DEBUG_EVENT));
};

/**
 * Hook for managing permissions debug mode
 *
 * Allows toggling debug info display for permission components via browser console.
 * Can be activated in any environment.
 *
 * @returns {Object} Debug state and utilities
 * @returns {boolean} returns.isDebugMode - Whether debug mode is active
 * @returns {() => void} returns.toggleDebug - Function to toggle debug mode
 *
 * @example
 * // In browser console:
 * window.__PERMISSIONS_DEBUG__ = true; window.dispatchEvent(new Event('permissions-debug-change'))
 * window.__togglePermissionsDebug__()  // Toggle debug (recommended)
 *
 * @example
 * // In component:
 * const { isDebugMode } = usePermissionsDebug();
 * {isDebugMode && <DebugInfo />}
 */
export const usePermissionsDebug = () => {
  const [isDebugMode, setIsDebugMode] = useState(() => {
    return typeof window !== 'undefined' && window.__PERMISSIONS_DEBUG__ === true;
  });

  // Listen for debug mode changes via custom event
  useEffect(() => {
    const handleDebugChange = () => {
      setIsDebugMode(window.__PERMISSIONS_DEBUG__ === true);
    };

    // Expose a helper function to toggle (only once)
    if (typeof window !== 'undefined' && !window.__togglePermissionsDebug__) {
      window.__togglePermissionsDebug__ = () => {
        window.__PERMISSIONS_DEBUG__ = !window.__PERMISSIONS_DEBUG__;
        notifyDebugChange();
        // eslint-disable-next-line no-console
        console.log(`🐛 Permissions Debug: ${window.__PERMISSIONS_DEBUG__ ? 'ON' : 'OFF'}`);
      };
    }

    window.addEventListener(PERMISSIONS_DEBUG_EVENT, handleDebugChange);

    return () => {
      window.removeEventListener(PERMISSIONS_DEBUG_EVENT, handleDebugChange);
    };
  }, []);

  const toggleDebug = useCallback(() => {
    window.__PERMISSIONS_DEBUG__ = !window.__PERMISSIONS_DEBUG__;
    notifyDebugChange();
  }, []);

  return {
    isDebugMode,
    toggleDebug,
  };
};
